import { ASSISTANT_BILL_UPLOAD_MIME_TYPES, MAX_FILES_PER_REQUEST } from '@orn/global/assistant/billUploadConstants.js';
import { AlertCircleIcon, CloseIcon, FileIcon, ImageIcon, LoadingIcon, PaperclipIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import sanitizeFilename from '@orn/shared-web/utils/sanitizeFilename';
import * as React from 'react';

import { uploadAttachment, UploadAttachmentError, validateAttachment } from '../../lib/assistant/uploadAttachment';

import type { IObservationDocument } from '@orn/global/assistant/snapshotMessage.js';

const MAX_TEXTAREA_HEIGHT_PX = 200;

// Pre-build the `accept` attribute from the shared whitelist so the OS
// file dialog hides unsupported types. Drag-drop bypasses `accept`, so the
// composer re-checks every file via `validateAttachment` before upload.
const ACCEPT_ATTR = Array.from(ASSISTANT_BILL_UPLOAD_MIME_TYPES).join(',');

// Fallback when a 429 arrives without a parseable `Retry-After` header.
// Matches the server's `ASSISTANT_UPLOAD_RATE_LIMIT.lockoutDurationMs`
// (1 hour) so a proxy-stripped header doesn't leave the toast self-clearing
// 59× earlier than the server-side lockout actually expires.
const RATE_LIMIT_FALLBACK_SECONDS = 60 * 60;

/**
 * Per-file state in the composer's local attachment list. Each chip rides
 * its own status machine — `uploading` flips to `ready` (when the upload
 * resolves) or `error` (server reject or local validation reject); a user
 * "remove" during upload triggers the `AbortController` and drops the
 * chip without surfacing an error.
 */
interface IComposerAttachment {
  localId: string;
  file: File;
  status: 'uploading' | 'ready' | 'error';
  documentId?: string;
  errorMessage?: string;
  abortController: AbortController;
}

interface IAssistantInputProps {
  /**
   * Called when the user submits the turn. The pane forwards to the WS
   * hook's `sendMessage`. `attachments` carries the resolved `{ id, name }`
   * rows for every uploaded document; the hook derives the wire
   * `attachmentDocumentIds` from them and reuses the same array for the
   * locally-echoed user bubble's chip row. Optional so the composer can
   * be wired to a noop / single-arg sender in stories.
   */
  onSend: (content: string, attachments?: IObservationDocument[]) => void;
  /**
   * True only while the in-flight turn's agent run is live and abortable. When
   * true the composer's primary button is a STOP button (`onStop`); otherwise
   * it is the normal send button. The non-abortable working window (optimistic
   * send, mid-session compaction) keeps the send button so there is never a
   * dead stop. Defaults to false so stories / non-live wirings stay send-only.
   */
  isAbortable?: boolean;
  /** Interrupt the in-flight turn. Invoked by the stop button when `isAbortable`. */
  onStop?: () => void;
  placeholder?: string;
}

/**
 * Assistant composer. Supports Enter to send (Shift+Enter for newline),
 * plus a paperclip / drag-drop affordance for bill-document attachments
 * (PDF + common image MIMEs, max 10 files × 48 MB each). Per-file uploads
 * fire on selection so chip status flips independently; the send button
 * gates on `all ready` before threading `documentIds` into the WS payload.
 *
 * The composer self-manages enable/disable on the send button (uploads in
 * flight → disabled). The WS hook queues sends until the connection is
 * handshake-ready, so the pane doesn't need to gate the composer's
 * `onSend` callback at all.
 */
const AssistantInput = ({
  onSend,
  isAbortable = false,
  onStop,
  placeholder = "Ask about your family's finances...",
}: IAssistantInputProps) => {
  const [value, setValue] = React.useState('');
  const [attachments, setAttachments] = React.useState<IComposerAttachment[]>([]);
  const [isDragging, setIsDragging] = React.useState(false);
  const [capError, setCapError] = React.useState<string | null>(null);
  const [rateLimitedUntil, setRateLimitedUntil] = React.useState<Date | null>(null);
  // Re-render tick — the toast countdown is derived from `Date.now()`, so
  // we bump this every 30s to update the "X min" copy without holding the
  // minutes in state (which would drift on every render).
  const [, setRateLimitTick] = React.useState(0);

  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dragDepthRef = React.useRef(0);
  const attachmentsRef = React.useRef<IComposerAttachment[]>(attachments);
  attachmentsRef.current = attachments;

  // Tick once a minute while the rate limit is active; clear `rateLimitedUntil`
  // the moment it expires so the toast disappears without a refresh.
  React.useEffect(() => {
    if (!rateLimitedUntil) {
      return;
    }
    const msUntilExpiry = rateLimitedUntil.getTime() - Date.now();
    if (msUntilExpiry <= 0) {
      setRateLimitedUntil(null);
      return;
    }
    const tick = window.setInterval(() => {
      if (rateLimitedUntil.getTime() <= Date.now()) {
        setRateLimitedUntil(null);
      } else {
        setRateLimitTick((n) => n + 1);
      }
    }, 30_000);
    return () => window.clearInterval(tick);
  }, [rateLimitedUntil]);

  // Auto-resize textarea as content grows
  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
    }
  }, [value]);

  // Abort any in-flight uploads when the composer unmounts (e.g. pane navigation).
  React.useEffect(
    () => () => {
      for (const chip of attachmentsRef.current) {
        if (chip.status === 'uploading') {
          chip.abortController.abort();
        }
      }
    },
    []
  );

  const startUpload = React.useCallback((file: File) => {
    const localId = crypto.randomUUID();
    const controller = new AbortController();
    setAttachments((prev) => [...prev, { localId, file, status: 'uploading', abortController: controller }]);

    void uploadAttachment(file, controller.signal)
      .then((res) => {
        setAttachments((prev) =>
          prev.map((a) => (a.localId === localId ? { ...a, status: 'ready', documentId: res.documentId } : a))
        );
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // Chip was already removed by the user (or the component unmounted);
          // no UI surface for cancellation. Drop silently.
          return;
        }
        if (err instanceof UploadAttachmentError && err.status === 429) {
          const seconds = err.retryAfterSeconds ?? RATE_LIMIT_FALLBACK_SECONDS;
          setRateLimitedUntil(new Date(Date.now() + seconds * 1000));
          // Chip message stays brief — the toast above the composer carries
          // the "try again in N min" detail, the chip just signals which
          // file was the one that got cut off.
          setAttachments((prev) =>
            prev.map((a) =>
              a.localId === localId ? { ...a, status: 'error', errorMessage: 'Rate limit reached.' } : a
            )
          );
          return;
        }
        const message = err instanceof UploadAttachmentError ? err.message : 'Upload failed.';
        setAttachments((prev) =>
          prev.map((a) => (a.localId === localId ? { ...a, status: 'error', errorMessage: message } : a))
        );
      });
  }, []);

  const handleFiles = React.useCallback(
    (files: FileList | File[]) => {
      setCapError(null);
      if (rateLimitedUntil && rateLimitedUntil.getTime() > Date.now()) {
        // Toast above the composer is already showing the cool-down deadline;
        // no point queueing more requests that will 429.
        return;
      }
      const filesArray = Array.from(files);
      if (attachmentsRef.current.length + filesArray.length > MAX_FILES_PER_REQUEST) {
        setCapError(
          `You can attach up to ${MAX_FILES_PER_REQUEST} files. Remove pending or errored files to add more.`
        );
        return;
      }
      for (const file of filesArray) {
        const localError = validateAttachment(file);
        if (localError) {
          // Insert an `error`-state chip immediately so the user sees the
          // rejection inline rather than just having the file silently
          // vanish into the picker.
          const localId = crypto.randomUUID();
          setAttachments((prev) => [
            ...prev,
            {
              localId,
              file,
              status: 'error',
              errorMessage: localError,
              abortController: new AbortController(),
            },
          ]);
          continue;
        }
        startUpload(file);
      }
    },
    [startUpload, rateLimitedUntil]
  );

  const removeAttachment = React.useCallback((localId: string) => {
    setAttachments((prev) => {
      const chip = prev.find((a) => a.localId === localId);
      if (chip?.status === 'uploading') {
        chip.abortController.abort();
      }
      return prev.filter((a) => a.localId !== localId);
    });
  }, []);

  const handleSubmit = () => {
    const trimmed = value.trim();
    const ready = attachments.filter(
      (a): a is IComposerAttachment & { documentId: string } => a.status === 'ready' && a.documentId !== undefined
    );
    const anyUploading = attachments.some((a) => a.status === 'uploading');
    if (anyUploading) {
      // Send button is disabled in this state — defensive guard.
      return;
    }
    if (!trimmed && ready.length === 0) {
      return;
    }

    onSend(
      trimmed,
      ready.map((a) => ({ id: a.documentId, name: a.file.name || null }))
    );
    setValue('');
    // Keep `error` chips so the user notices the failures. Drop everything else.
    setAttachments((prev) => prev.filter((a) => a.status === 'error'));
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
    // Reset so re-picking the same file still fires `change`.
    e.target.value = '';
  };

  // `dragenter` fires once per nested element the cursor crosses, so we
  // count enters and leaves and only clear `isDragging` when the depth
  // returns to zero. Otherwise the highlight flickers as the user moves
  // over children of the wrapper.
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      dragDepthRef.current += 1;
      setIsDragging(true);
    }
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragging(false);
    }
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
    }
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const anyUploading = attachments.some((a) => a.status === 'uploading');
  const readyCount = attachments.filter((a) => a.status === 'ready').length;
  const canSend = !anyUploading && (value.trim().length > 0 || readyCount > 0);
  const isRateLimited = rateLimitedUntil !== null && rateLimitedUntil.getTime() > Date.now();
  const isPickerCapped = attachments.length >= MAX_FILES_PER_REQUEST || isRateLimited;

  return (
    <div className="flex flex-col gap-2">
      {isRateLimited && rateLimitedUntil !== null && <RateLimitToast retryAt={rateLimitedUntil} />}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cx(
          'bg-primary border-secondary flex flex-col gap-2 rounded-2xl border p-3 shadow-sm transition-colors',
          isDragging && 'border-brand-solid bg-brand-secondary'
        )}
      >
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {attachments.map((chip) => (
              <AttachmentChip key={chip.localId} chip={chip} onRemove={() => removeAttachment(chip.localId)} />
            ))}
          </div>
        )}
        {capError && <div className="text-error-primary text-xs">{capError}</div>}
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isPickerCapped}
            className={cx(
              'flex size-8 shrink-0 items-center justify-center rounded-full transition-colors',
              isPickerCapped
                ? 'text-disabled cursor-not-allowed'
                : 'text-tertiary hover:bg-secondary hover:text-secondary cursor-pointer'
            )}
            aria-label="Attach files"
          >
            <PaperclipIcon className="size-5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            multiple
            onChange={handleFileInputChange}
            className="hidden"
          />
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            className={cx(
              'placeholder:text-placeholder text-primary flex-1 resize-none bg-transparent text-sm outline-none',
              'max-h-[200px] min-h-[24px]'
            )}
          />
          {isAbortable ? (
            <button
              type="button"
              onClick={onStop}
              className="border-secondary text-brand-primary hover:bg-secondary bg-primary flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-colors"
              aria-label="Stop"
            >
              <StopIcon className="size-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSend}
              className={cx(
                'flex size-8 shrink-0 items-center justify-center rounded-full transition-colors',
                canSend
                  ? 'bg-brand-solid hover:bg-brand-solid_hover cursor-pointer text-white'
                  : 'bg-disabled text-disabled cursor-not-allowed'
              )}
              aria-label="Send message"
            >
              <SendIcon className="size-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface IRateLimitToastProps {
  retryAt: Date;
}

/**
 * Notice rendered above the composer when `/assistant/upload` returns 429.
 * Reads `Date.now()` at render time and rounds up to the next whole minute
 * so the copy never says "0 min" while still under cool-down.
 */
const RateLimitToast = ({ retryAt }: IRateLimitToastProps) => {
  const minutesRemaining = Math.max(1, Math.ceil((retryAt.getTime() - Date.now()) / 60_000));
  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-warning-primary text-warning-primary flex items-start gap-2 rounded-lg px-3 py-2 text-sm"
    >
      <AlertCircleIcon className="mt-0.5 size-4 shrink-0" />
      <div className="flex-1">
        <div className="font-medium">Too many uploads</div>
        <div className="text-xs">
          You can attach more files in {minutesRemaining} {minutesRemaining === 1 ? 'minute' : 'minutes'}.
        </div>
      </div>
    </div>
  );
};

interface IAttachmentChipProps {
  chip: IComposerAttachment;
  onRemove: () => void;
}

/**
 * Single chip rendered in the preview row. Status drives the leading icon
 * (spinner / type icon / red alert) and the border color; the filename
 * is sanitized + truncated so a 500-char or control-char-laden name
 * cannot break the layout.
 */
const AttachmentChip = ({ chip, onRemove }: IAttachmentChipProps) => {
  const isImage = chip.file.type.startsWith('image/');
  const TypeIcon = isImage ? ImageIcon : FileIcon;
  const safeName = sanitizeFilename(chip.file.name, { fallback: 'Untitled' });

  return (
    <div
      className={cx(
        'bg-secondary border-primary flex max-w-xs items-center gap-2 rounded-lg border px-2 py-1.5',
        chip.status === 'error' && 'border-error-primary'
      )}
    >
      {chip.status === 'uploading' ? (
        <LoadingIcon className="text-brand-solid size-4 shrink-0 animate-spin" />
      ) : chip.status === 'error' ? (
        <AlertCircleIcon className="text-error-primary size-4 shrink-0" />
      ) : (
        <TypeIcon className="text-tertiary size-4 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-primary truncate text-xs font-medium" title={chip.file.name}>
          {safeName}
        </div>
        {chip.status === 'error' && chip.errorMessage && (
          <div className="text-error-primary mt-0.5 text-xs">{chip.errorMessage}</div>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="text-tertiary hover:text-primary flex size-5 shrink-0 items-center justify-center rounded transition-colors"
        aria-label={`Remove ${chip.file.name || 'attachment'}`}
      >
        <CloseIcon className="size-3.5" />
      </button>
    </div>
  );
};

const SendIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

// Filled rounded square — the conventional "stop" glyph. Inline like SendIcon
// (no StopIcon in @orn/shared-web/icons); `currentColor` so it inherits the
// button's brand-blue icon on a white fill.
const StopIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

export default AssistantInput;
